// ═══════════════════════════════════════════════════════
// Pathfinder — A* Algorithmus
// KEIN Phaser-Import! Vollständig unit-testbar.
// ═══════════════════════════════════════════════════════
import type { GridCoord } from '@interfaces/index';

export interface PathGrid {
  cols: number;
  rows: number;
  isWalkable(x: number, y: number): boolean;
}

export interface PathResult {
  path: GridCoord[];
  length: number;
}

/**
 * A* Pathfinding — findet kürzesten Weg von start zu end.
 * Gibt null zurück wenn kein Pfad existiert.
 */
export function findPath(
  grid: PathGrid,
  start: GridCoord,
  end: GridCoord
): PathResult | null {
  const { cols, rows } = grid;
  const key = (x: number, y: number): number => y * cols + x;
  const valid = (x: number, y: number): boolean =>
    x >= 0 && y >= 0 && x < cols && y < rows && grid.isWalkable(x, y);
  const h = (x: number, y: number): number =>
    Math.abs(x - end.gx) + Math.abs(y - end.gy);

  const open = new Map<number, { x: number; y: number; f: number }>();
  const closed = new Set<number>();
  const g: Record<number, number> = {};
  const parent: Record<number, number | undefined> = {};

  const sk = key(start.gx, start.gy);
  const ek = key(end.gx, end.gy);
  g[sk] = 0;
  open.set(sk, { x: start.gx, y: start.gy, f: h(start.gx, start.gy) });

  const DIRS: [number, number][] = [[0, -1], [1, 0], [0, 1], [-1, 0]];
  let iterations = 0;
  const MAX_ITERATIONS = cols * rows * 2;

  while (open.size > 0 && iterations++ < MAX_ITERATIONS) {
    // Find lowest f score
    let cur: number | null = null;
    let cf = Infinity;
    for (const [k, v] of open) {
      if (v.f < cf) { cf = v.f; cur = k; }
    }
    if (cur === null) break;

    if (cur === ek) {
      // Reconstruct path
      const path: GridCoord[] = [];
      let c: number | undefined = cur;
      while (c !== undefined) {
        path.unshift({ gx: c % cols, gy: Math.floor(c / cols) });
        c = parent[c];
      }
      return { path, length: path.length };
    }

    const node = open.get(cur)!;
    open.delete(cur);
    closed.add(cur);

    for (const [dx, dy] of DIRS) {
      const nx = node.x + dx;
      const ny = node.y + dy;
      const nk = key(nx, ny);
      if (!valid(nx, ny) || closed.has(nk)) continue;
      const ng = g[cur] + 1;
      if (open.has(nk) && ng >= g[nk]) continue;
      g[nk] = ng;
      parent[nk] = cur;
      open.set(nk, { x: nx, y: ny, f: ng + h(nx, ny) });
    }
  }

  return null; // No path found
}

/**
 * Prüft ob ein Pfad existiert ohne den vollständigen Pfad zu berechnen.
 * Schneller als findPath wenn nur Erreichbarkeit geprüft werden soll.
 */
export function hasPath(
  grid: PathGrid,
  start: GridCoord,
  end: GridCoord
): boolean {
  return findPath(grid, start, end) !== null;
}
